// @flow

import find from 'lodash/find';
import React, { useCallback, useRef } from 'react';
import type { Node, RefObject } from 'react';
import { Section, Wrapper, wrapperPaddings } from '@riseart/layout';
import { SliderPagination, scrollToElement } from '@riseart/common';
import { gridSectionCls, GridCounter, GridHeader, GridPaginationWrapper } from '@riseart/grid';
import { isTouchDevice } from '@riseart/fe-utils';
import { components as COMPONENTS_CONFIG } from 'Config';
import {
  art as ART_ENUM,
  pagination as PAGINATION_ENUM,
  analytics as ENUM_ANALYTICS,
  meta as META_ENUM,
} from 'Enum';
import { SliderPaginationCounter } from 'shared_components/common/pagination/counter/Slider';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { Pagination } from 'shared_hocs/pagination/Pagination';
import { ArtGridFluid } from 'shared_components/arts/containers/grid/Fluid';
import { LDTYPES, generateLDJSON } from 'shared_models/seo/ldJSON';
import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { generateSrcForCDN } from 'shared_components/common/artdirection/picture/Picture';
import { LocationManager } from 'shared_services/riseart/url/Location';

type Props = {
  loading: boolean,
  items: Array<Object>,
  lazyloadAfter?: number,
  pagination: Object,
  pageUrl: string,
  discardPagination?: boolean,
  sortComponent: Node,
  layoutSwitchComponent: Node,
  additionalActionControls: Function,
  includeLDJSON?: boolean,
};

/**
 * ArtistArtworkFluidGrid
 *
 * @param {Props} props
 * @returns Node
 */
export function ArtistArtworkFluidGrid({
  sortComponent,
  layoutSwitchComponent,
  loading,
  lazyloadAfter,
  items,
  pagination,
  pageUrl,
  additionalActionControls,
  discardPagination = false,
  includeLDJSON = false,
}: Props): Node {
  const RIPPLE_CONFIG = {
    small: { backgroundPositionY: 118 },
    medium: { backgroundPositionY: 118 },
    large: { backgroundPositionY: 118 },
  };
  const FALLBACK_ART_SQUARE = find(COMPONENTS_CONFIG.art.fallbackImages, {
    type: ART_ENUM.image.type.TYPE_MAIN_SQUARE,
  });
  const origin = LocationManager.get('origin');
  const gridRef: RefObject<HTMLDivElement | null> = useRef(null);
  const handleScrollToTopOfGrid = useCallback(() => scrollToElement(gridRef), [gridRef]);

  return (
    <IsomorphicRipple isActive={loading} styleConfig={RIPPLE_CONFIG}>
      <div ref={gridRef} />
      <Section className={gridSectionCls}>
        <Wrapper>
          <GridHeader>
            {sortComponent}
            <div>
              {pagination.currentPage ? (
                <GridCounter>
                  <SliderPaginationCounter
                    currentPage={pagination.currentPage}
                    itemsPerPage={pagination.itemsPerPage}
                    totalItems={pagination.totalItems}
                  />
                </GridCounter>
              ) : null}
              {layoutSwitchComponent}
            </div>
          </GridHeader>
          <ArtGridFluid
            loading={loading}
            items={items}
            imageType={ART_ENUM.list.LAYOUT_FLUID}
            wrapperPaddings={wrapperPaddings}
            cardDisplayStyle={6}
            openInNewTab={false}
            showAddToCart
            disableHeadingTags
            showHoverContent={!isTouchDevice()}
            lazyloadAfter={lazyloadAfter}
            tracking={{
              name: ENUM_ANALYTICS.list.name.ARTIST_ART_GRID,
              enabled: true,
              enableInitial: true,
              enableClick: true,
            }}
            additionalActionControls={
              additionalActionControls && typeof additionalActionControls === 'function'
                ? additionalActionControls
                : null
            }
          />
          {items && items.length > 0 && !discardPagination ? (
            <GridPaginationWrapper>
              <Pagination
                paginationType={PAGINATION_ENUM.type.SLIDER}
                current={pagination.currentPage}
                total={pagination.totalItems}
                totalPages={pagination.totalPages}
                pageSize={pagination.itemsPerPage}
                pageUrl={pageUrl}
                beforeChange={handleScrollToTopOfGrid}
                isLoading={loading}
                offset={{ top: 0, left: 0, bottom: -1000, right: 0 }}
              >
                {(props) => <SliderPagination {...props} />}
              </Pagination>
            </GridPaginationWrapper>
          ) : null}
        </Wrapper>
      </Section>
      {includeLDJSON && items && items.length ? (
        <MetaProvider
          cacheState={false}
          meta={{
            [META_ENUM.METATYPE.SCRIPT_LD_JSON_ARTWORKS_LIST]: generateLDJSON(
              LDTYPES.CAROUSEL_ART,
              items.map((i: Object) => ({
                ...i,
                url: UrlAssembler.artDetail(i.id, i.slug, { origin }),
                artistUrl: i.artistId
                  ? UrlAssembler.artistProfile(i.artistId, i.artistAlias, { origin })
                  : null,
                image: generateSrcForCDN({
                  ...(find(i.images, {
                    type: ART_ENUM.image.type.TYPE_FLAT_SQUARE,
                  }) || FALLBACK_ART_SQUARE),
                  width: 1200,
                }),
              })),
            ),
          }}
        />
      ) : null}
    </IsomorphicRipple>
  );
}
